import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { EditorState } from "@tiptap/pm/state";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

import { findPublicIds, type PublicIdHrefResolver } from "../../lib/public-id-links";

/** View-only links over bare public ids — a Link mark would serialize into the stored value. */
export const publicIdLinkPluginKey = new PluginKey("publicIdLinkDecoration");

/** Ref, not a config value: `useEditor` builds extensions once; the resolver changes. */
export type PublicIdLinkResolverRef = { current: PublicIdHrefResolver | null };

function buildDecorations(doc: ProseMirrorNode, ref: PublicIdLinkResolverRef): DecorationSet {
  const resolve = ref.current;
  if (!resolve) return DecorationSet.empty;
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.type.name === "codeBlock") return false;
    if (!node.isText || !node.text) return;
    if (node.marks.some((mark) => mark.type.name === "link" || mark.type.name === "code")) return;
    for (const match of findPublicIds(node.text)) {
      const href = resolve(match.id, match.type);
      if (!href) continue;
      decorations.push(
        Decoration.inline(pos + match.start, pos + match.end, {
          nodeName: "a",
          href,
          target: "_blank",
          rel: "noopener noreferrer",
          class: "quovy-public-id-link",
          title: `Open ${match.type.replace(/_/g, " ")} ${match.id}`,
        }),
      );
    }
  });
  return DecorationSet.create(doc, decorations);
}

export function PublicIdLinkDecoration(ref: PublicIdLinkResolverRef) {
  return Extension.create({
    name: "publicIdLinkDecoration",
    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: publicIdLinkPluginKey,
          state: {
            init: (_config, state: EditorState) => buildDecorations(state.doc, ref),
            apply: (tr, value: DecorationSet, _oldState, newState) =>
              tr.docChanged ? buildDecorations(newState.doc, ref) : value,
          },
          props: {
            decorations(state) {
              return publicIdLinkPluginKey.getState(state) as DecorationSet | undefined;
            },
            handleClick(view, _pos, event) {
              const target = (event.target as HTMLElement | null)?.closest?.(
                "a.quovy-public-id-link",
              );
              if (!target) return false;
              const href = target.getAttribute("href");
              if (!href) return false;
              window.open(href, "_blank", "noopener,noreferrer");
              event.preventDefault();
              return true;
            },
          },
        }),
      ];
    },
  });
}
